import { useState } from "react";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { LoadingButton } from "@/components/ui/loading-button";
import { useNotification } from "@/hooks/useNotification";
import { forgetPassword } from "@/lib/auth-client";
import { PATHS } from "@/config/paths";
import { useTranslation } from "@/hooks/useTranslation";

const createLoginFormSchema = (t: (key: string) => string) =>
  z.object({
    email: z.string().email(t("forms.invalid_email")),
  });

export type ForgotPasswordValues = z.infer<ReturnType<typeof createLoginFormSchema>>;

export const ForgotPasswordForm = () => {
  const [loadingResetPassword, setLoadingResetPassword] = useState(false);
  const { showNotification } = useNotification();
  const { t } = useTranslation();

  const loginFormSchema = createLoginFormSchema((key: string) => t(key) as string);

  const form = useForm({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const handleSubmit = (values: ForgotPasswordValues) => {
    setLoadingResetPassword(true);
    forgetPassword({
      email: values.email,
      redirectTo: PATHS.RESET_PASSWORD,
    })
      .catch((error) => {
        showNotification({
          type: "error",
          message: t("account.update_password.reset_error_title"),
          description: t("account.update_password.reset_error_description"),
        });
      })
      .then(() => {
        showNotification({
          type: "success",
          message: t("account.update_password.reset_sent_title"),
          description: t("account.update_password.reset_sent_description"),
        });
      })
      .finally(() => {
        setLoadingResetPassword(false);
      });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="mb-4">
              <FormLabel>{t("forms.email")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("forms.email_placeholder")}
                  required
                  autoFocus
                  {...field}
                  {...form.register("email")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <LoadingButton
          data-umami-event="forgot-password-form-submit"
          type="submit"
          loading={loadingResetPassword}
          className="w-full"
        >
          {t("forms.reset_password")}
        </LoadingButton>
      </form>
    </Form>
  );
};
